import {
  TrasferimentoModalita,
  useGetModelliDispositivo,
  useGetNodo,
  useGetNodoPortale,
} from "@/api";
import {
  PosizioneFilter,
  usePosizioneFilter,
} from "@/hooks/usePosizioneFilter";
import { portaleConfig } from "@/portaliConfig";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function usePosizioneFilterWithNavigation(
  baseUrl: string,
  options?: {
    enableNodo?: boolean;
    enableTitolare?: boolean;
    enableModalita?: boolean;
    enablePagination?: boolean;
    enableDate?: boolean;
    enableMatricola?: boolean;
    enableModello?: boolean;
    enableTextFilter?: boolean;
  }
) {
  const enableNodo = options?.enableNodo ?? false;
  const enableTitolare = options?.enableTitolare ?? false;
  const enableModalita = options?.enableModalita ?? false;
  const enablePagination = options?.enablePagination ?? false;
  const enableDate = options?.enableDate ?? false;
  const enableMatricola = options?.enableMatricola ?? false;
  const enableModello = options?.enableModello ?? false;
  const enableTextFilter = options?.enableTextFilter ?? false;

  const { search: queryString } = useLocation();

  const {
    idNodo,
    idTitolare,
    modalita,
    page: _page,
    pageSize: _pageSize,
    date: _date,
    matricola: _matricola,
    idModello,
    textFilter: _textFilter,
  } = useMemo(() => {
    const parsed = new URLSearchParams(queryString);
    return Object.fromEntries(parsed.entries());
  }, [queryString]);

  const [page, setPage] = useState(parseInt(_page) || 0);
  const [pageSize, setPageSize] = useState(parseInt(_pageSize) || 20);
  const [filters, changeFilter, setFilters] = usePosizioneFilter(() => {
    setPage(0);
  });

  const justMountedRef = useRef<boolean>(true);

  const nodo = useGetNodoPortale(portaleConfig.id, parseInt(idNodo ?? "0"), {
    query: {
      enabled: enableNodo && !!idNodo,
    },
  });

  const titolare = useGetNodo(parseInt(idTitolare ?? "0"), {
    query: { enabled: justMountedRef.current && !!idTitolare },
  });

  const modelli = useGetModelliDispositivo({
    query: {
      enabled: enableModello && !!idModello,
    },
  });

  // url -> filter
  useEffect(() => {
    if (!justMountedRef.current) {
      return;
    }

    const loadingCompleted =
      (!enableNodo || !idNodo || nodo.data?.data) &&
      (!enableTitolare || !idTitolare || titolare.data?.data) &&
      (!enableModello || !idModello || modelli.data?.data);

    if (loadingCompleted) {
      const filter: PosizioneFilter = {
        nodo: enableNodo ? nodo.data?.data ?? null : null,
        titolare: enableTitolare ? titolare.data?.data ?? null : null,
        modalita: enableModalita ? (modalita as TrasferimentoModalita) : null,
        date: enableDate
          ? _date && moment(_date, "YYYY-MM-DD").isValid()
            ? moment(_date, "YYYY-MM-DD").startOf("day").toDate()
            : null
          : null,
        matricola: enableMatricola ? _matricola : null,
        modello:
          enableModello && idModello
            ? modelli.data?.data.find((x) => x.id === parseInt(idModello)) ??
              null
            : null,
        textFilter: enableTextFilter ? _textFilter || null : null,
      };

      setFilters(filter);
      justMountedRef.current = false;
    }
  }, [
    idNodo,
    idTitolare,
    enableNodo,
    enableTitolare,
    enableModalita,
    modalita,
    nodo,
    idModello,
    titolare.data,
    enableMatricola,
    enableModalita,
    enableTextFilter,
    modelli.data,
    justMountedRef,
    setFilters,
  ]);

  // filter -> url
  const navigate = useNavigate();
  useEffect(() => {
    if (justMountedRef.current) {
      return;
    }

    const params = new URLSearchParams();
    if (filters.nodo && filters.nodo) {
      params.set("idNodo", filters.nodo.id.toString());
    }
    if (enableTitolare && filters.titolare) {
      params.set("idTitolare", filters.titolare.id.toString());
    }
    if (enableModalita && filters.modalita) {
      params.set("modalita", filters.modalita);
    }

    if (enablePagination) {
      params.set("page", page.toString());
      params.set("pageSize", pageSize.toString());
    }

    if (enableDate && filters.date) {
      params.set(
        "date",
        moment(filters.date).startOf("day").format("YYYY-MM-DD")
      );
    }

    if (enableMatricola && filters.matricola) {
      params.set("matricola", filters.matricola);
    }

    if (enableModello && filters.modello) {
      params.set("idModello", filters.modello.id.toString());
    }

    if (enableTextFilter && filters.textFilter) {
      params.set("textFilter", filters.textFilter);
    }

    const url = `${baseUrl}?${params.toString()}`;

    if (url !== baseUrl) {
      navigate(url);
    }
  }, [
    filters,
    navigate,
    baseUrl,
    enableNodo,
    enableTitolare,
    enableModalita,
    enableDate,
    enableMatricola,
    enableModello,
    enableTextFilter,
    enablePagination,
    page,
    pageSize,
  ]);

  return {
    filters,
    changeFilter,
    filtersLoading: justMountedRef.current,
    page,
    pageSize,
    setPage,
    setPageSize,
  } as const;
}
